'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Image from 'next/image';
import styles from '../styles/Features.module.css';

gsap.registerPlugin(ScrollTrigger); // Register plugin outside component

const Features = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const featureRefs = useRef([]);

  useEffect(() => {
    // Create gsap context for scoping and automatic cleanup
    const ctx = gsap.context(() => {
      const section = sectionRef.current;
      const heading = headingRef.current;
      const features = featureRefs.current;
      
      if (!section || !heading || features.length === 0) return;
      
      // Create a timeline for the animations
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top bottom-=100',
          end: 'bottom top',
          toggleActions: 'play none none reverse',
        }
      });
      
      // Animate the heading
      tl.fromTo(heading, 
        { y: -30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' }
      );
      
      // Animate the features
      tl.fromTo(features, 
        { y: 50, opacity: 0 },
        { 
          y: 0, 
          opacity: 1, 
          stagger: 0.2, 
          duration: 1, 
          ease: 'power3.out' 
        },
        '-=0.2'
      );
    }, sectionRef); // Scope the context to the section element
    
    // Return ctx.revert() for automatic cleanup
    return () => ctx.revert();
  }, []);

  return (
    <section className={styles.features} ref={sectionRef} id="features">
      <div className={styles.featuresHeading} ref={headingRef}>
        <h2>Why GameMax?</h2>
        <p>The ultimate platform for pickleball enthusiasts</p>
      </div>
      
      <div className={styles.featuresContainer}>
        <div 
          className={styles.featureItem} 
          ref={el => featureRefs.current[0] = el}
        >
          <div className={styles.featureOverlay}>
            <h4>Play & Earn Ecosystem</h4>
            <p>
              Join 100,000+ users in Paddle Battle LE and mobile app, earn Pickles and Dinks, convert to GAM tokens, and redeem rewards in our marketplace.
            </p>
            <div className={styles.featureTag}>
              <span>Play & Earn</span>
            </div>
          </div>
          <Image 
            src="https://imagedelivery.net/LOBxX7gyPpyC22XVZKaoOw/f66780f9-3d94-405e-3f6f-c516cbe99f00/public" 
            alt="Play & Earn Ecosystem"
            width={500}
            height={800}
            className={styles.featureImage}
          />
        </div>
        
        <div 
          className={styles.featureItem} 
          ref={el => featureRefs.current[1] = el}
        >
          <div className={styles.featureOverlay}>
            <h4>AI-Powered Training</h4>
            <p>
              Sharpen your skills with AI coaching, advanced analytics, and global matches for players.
            </p>
            <div className={styles.featureTag}>
              <span>AI Coaching</span>
            </div>
          </div>
          <Image 
            src="https://imagedelivery.net/LOBxX7gyPpyC22XVZKaoOw/4dd365ea-9518-41f5-0dd5-476b71a7fb00/public" 
            alt="AI-Powered Training"
            width={500}
            height={800}
            className={styles.featureImage}
          />
        </div>

        <div 
          className={styles.featureItem} 
          ref={el => featureRefs.current[2] = el}
        >
          <div className={styles.featureOverlay}>
            <h4>Global Community</h4>
            <p>
              Find courts, schedule matches, create events, and connect with a thriving pickleball community.
            </p>
            <div className={styles.featureTag}>
              <span>Community</span>
            </div>
          </div>
          <Image 
            src="https://imagedelivery.net/LOBxX7gyPpyC22XVZKaoOw/bb3e7a22-7cab-487f-4f75-2b30bf5c3a00/public" 
            alt="Global Community Hub"
            width={500}
            height={800}
            className={styles.featureImage}
          />
        </div>
        <div 
          className={styles.featureItem} 
          ref={el => featureRefs.current[3] = el}
        >
          <div className={styles.featureOverlay}>
            <h4>Tokenized Marketplace</h4>
            <p>
              Use GAM tokens to shop branded merchandise, access premium features, and earn rewards in our integrated marketplace.
            </p>
            <div className={styles.featureTag}>
              <span>Marketplace</span>
            </div>
          </div>
          <Image 
            src="https://imagedelivery.net/LOBxX7gyPpyC22XVZKaoOw/9abc3d70-34b4-44ed-99af-095c26c6de00/public" 
            alt="Tokenized Marketplace"
            width={500}
            height={800}
            className={styles.featureImage}
          />
        </div>
      </div>
    </section>
  );
};

export default Features; 
